import {
  getActivityTemplates,
  DEFAULT_GAME_LOOP_DISPLAY_MINUTES,
  DEFAULT_GAME_SLOT_LENGTH_SECONDS,
  getActivityTemplateByType
} from '../constants/activities'
import {
  DEFAULT_DISPLAY_BUFFER_MINUTES,
  FIRST_GAME_INTRO_MINUTES,
  sanitizeSprintActivityGameOptions
} from '../../../../../../pure-js/libs/SprintActivityGameOptions'
import { isGameActivity } from '../config'
import { Activity, ActivityType } from '../types'
import { getActivityTypeLabel, type TranslationFunction } from '../../../shared/helpers/liveActivityHelpers'

export interface ActivityTypeOption {
  value: ActivityType
  label: string
}

export interface DurationOption {
  value: number
  label: string
}

const DEFAULT_MAX_GAME_LOOPS = 3

export const getMaxLoopCount = (type: ActivityType): number => {
  if (!shouldShowLoopConfiguration(type)) {
    return 1
  }

  const templateLoopCount = getActivityTemplateByType(type)?.numberOfLoops ?? DEFAULT_MAX_GAME_LOOPS

  return Math.max(1, Math.min(templateLoopCount, DEFAULT_MAX_GAME_LOOPS))
}

export const getLoopOptions = (type: ActivityType): number[] => {
  if (!shouldShowLoopConfiguration(type)) {
    return []
  }

  return Array.from({ length: getMaxLoopCount(type) }, (_, index) => index + 1)
}

/**
 * Generate a unique activity id for sprint creation flows.
 *
 * @param {ActivityType} type - Activity type prefix.
 * @return {string} Unique activity id.
 */
export const generateActivityId = (type: ActivityType) =>
  `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

/**
 * Build translated activity type options for selects.
 *
 * @param {TranslationFunction} t - Translation function.
 * @param {boolean} isCompletedSprint - Restrict options for completed sprints.
 * @return {ActivityTypeOption[]} Select options.
 */
export const getActivityTypeOptions = (
  t: TranslationFunction,
  isCompletedSprint: boolean = false
): ActivityTypeOption[] => {
  // For completed sprints only short reflection is allowed. Return directly
  // without depending on the async template cache so the dropdown is always
  // populated when this modal opens from the sprint details page.
  if (isCompletedSprint) {
    return [{ value: ActivityType.SHORT_REFLECTION, label: getActivityTypeLabel(ActivityType.SHORT_REFLECTION, t) }]
  }

  return getActivityTemplates().map((activity) => ({
    value: activity.type,
    label: getActivityTypeLabel(activity.type, t)
  }))
}

/**
 * Build translated duration options for activity forms.
 *
 * @param {TranslationFunction} t - Translation function.
 * @return {DurationOption[]} Duration options.
 */
export const getDurationOptions = (t: TranslationFunction, type?: ActivityType): DurationOption[] => {
  const allOptions: DurationOption[] = [
    { value: 30, label: t('common.30minutes') },
    { value: 45, label: t('common.45minutes') },
    { value: 75, label: t('common.75minutes', '75 minutes') },
    { value: 60, label: t('common.1hour') },
    { value: 90, label: t('common.90minutes') },
    { value: 120, label: t('common.2hours') },
    { value: 180, label: t('common.3hours') }
  ]

  if (type && shouldShowLoopConfiguration(type)) {
    const resolvedDuration = getResolvedActivityDuration(type)
    return allOptions.filter((option) => option.value === resolvedDuration)
  }

  return allOptions
}

/**
 * Read the default duration from the activity template.
 *
 * @param {ActivityType} type - Activity type.
 * @return {number} Default duration in minutes.
 */
export const getDefaultDurationValue = (type: ActivityType): number => {
  const template = getActivityTemplateByType(type)
  return template?.duration ?? 60
}

/**
 * Resolve the effective activity duration for scheduling.
 *
 * Game activities use fixed durations derived from their templates so the
 * schedule matches the runtime config.
 *
 * @param {ActivityType} type - Activity type.
 * @param {number | undefined} duration - Explicit duration override.
 * @return {number} Resolved duration in minutes.
 */
export const getResolvedActivityDuration = (type: ActivityType, duration?: number): number =>
  shouldShowLoopConfiguration(type) ? getDefaultDurationValue(type) : (duration ?? getDefaultDurationValue(type))

/**
 * Resolve loop count from explicit value or activity template default.
 *
 * @param {ActivityType} type - Activity type.
 * @param {number | undefined} numberOfLoops - Explicit loop count.
 * @return {number | undefined} Loop count.
 */
export const getResolvedLoopCount = (type: ActivityType, numberOfLoops?: number): number | undefined =>
  shouldShowLoopConfiguration(type)
    ? Math.min(
        Math.max(
          1,
          sanitizeSprintActivityGameOptions({ numberOfLoops }).numberOfLoops ??
            sanitizeSprintActivityGameOptions(getActivityTemplateByType(type)).numberOfLoops ??
            getMaxLoopCount(type)
        ),
        getMaxLoopCount(type)
      )
    : undefined

/**
 * Returns slot + display buffer in minutes for facilitator/BO display.
 * Game and backend use slot only (customSlotLength in seconds); this is for planning UI only.
 * @param customSlotLength - Slot length in seconds (runtime).
 * @param displayBufferMinutes - Optional buffer minutes from config (default 5).
 * @returns Displayed slot length in minutes (slot + buffer).
 */
export const getSlotLengthMinutes = (customSlotLength?: number, displayBufferMinutes?: number | null): number => {
  const slotMin = Math.round((customSlotLength ?? DEFAULT_GAME_SLOT_LENGTH_SECONDS) / 60)
  return slotMin + (displayBufferMinutes ?? DEFAULT_DISPLAY_BUFFER_MINUTES)
}

/**
 * Display duration in minutes for facilitator UI (planning, cards).
 * For game activities: intro (if FIRST_GAME) + loops × (slot + buffer); otherwise activity.duration.
 *
 * @param activity - Activity with type, duration, and optional numberOfLoops.
 * @returns Duration in minutes to show in the UI.
 */
export const getDisplayDurationMinutes = (activity: {
  type: ActivityType
  duration: number
  numberOfLoops?: number
}): number => {
  if (!shouldShowLoopConfiguration(activity.type)) return activity.duration
  const template = getActivityTemplateByType(activity.type)
  const loops = getResolvedLoopCount(activity.type, activity.numberOfLoops) ?? 1
  const slotMins = getSlotLengthMinutes(template?.customSlotLength, template?.slotDisplayBufferMinutes)
  const introMins = activity.type === ActivityType.FIRST_GAME ? FIRST_GAME_INTRO_MINUTES : 0
  return introMins + loops * slotMins
}

/**
 * Return the facilitator-facing planning time shown per loop.
 *
 * The game runtime slot is 15 minutes, but sprint planning continues to
 * present loops as 20-minute chunks to account for the surrounding flow.
 *
 * @return {number} Displayed loop planning time in minutes.
 */
export const getLoopPlanningMinutes = (): number => DEFAULT_GAME_LOOP_DISPLAY_MINUTES

/**
 * Translate the default activity titles used in the add-activity grid.
 *
 * @param {TranslationFunction} t - Translation function.
 * @return {Activity[]} Translated activity templates.
 */
export const getTranslatedAvailableActivities = (t: TranslationFunction): Activity[] =>
  getActivityTemplates().map((activity) => ({
    ...activity,
    title: getActivityTypeLabel(activity.type, t)
  }))

/**
 * Create a concrete activity instance from a template for sprint creation.
 *
 * @param {ActivityType} type - Activity type to instantiate.
 * @param {TranslationFunction} t - Translation function.
 * @param {Partial<Activity>} overrides - Activity overrides.
 * @return {Activity | null} Instantiated activity or null when template is missing.
 */
export const createActivityInstanceFromTemplate = (
  type: ActivityType,
  t: TranslationFunction,
  overrides: Partial<Activity> = {}
): Activity | null => {
  const template = getActivityTemplateByType(type)

  if (!template) {
    return null
  }

  return {
    ...template,
    id: overrides.id ?? generateActivityId(type),
    title: overrides.title ?? getActivityTypeLabel(type, t),
    ...overrides
  }
}

/**
 * Check whether a frontend activity type should expose loop controls.
 *
 * @param {ActivityType} type - Activity type.
 * @return {boolean} True when the activity is a game.
 */
export const shouldShowLoopConfiguration = (type: ActivityType): boolean => isGameActivity(type)
